import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from './Modal'
import { GetChannelCategories, GetViewFilters, GetViewsDistributionList } from '../graphql/queries'
import { useMutation, useQuery } from '@apollo/client'
import SelectInput from './SelectInput'
import Input from './Input'
import Button from './Button'
import { AddCreationViews } from '../graphql/mutations'
import ErrorMessage from './ErrorMessage'
import SuccessMessage from './SuccessMessage'

const InitialErrorsState = {
    count: null,
    distribution: null,
    channelCategory: null,
    generic: null,
}

const AddCreationViewsModal = ({
    show,
    close,
    creation,
}) => {
    const { t } = useTranslation()
    const { data } = useQuery(GetViewsDistributionList)
    const { data: channelCategoriesData } = useQuery(GetChannelCategories)
    const [addCreationViews, { loading }] = useMutation(AddCreationViews)

    const [distribution, setDistribution] = useState(null)
    const [channelCategory, setChannelCategory] = useState(null)
    const [count, setCount] = useState('')

    const [success, setSuccess] = useState(null)
    const [errors, setErrors] = useState(InitialErrorsState)

    const distributions = (data?.getViewsDistributionList || [])
        .map(x => ({
            value: x.distribution,
            label: x.distribution,
            type: x.type,
            year: x.year,
            country: x.country,
        }))
        .sort((a, b) => {
            const yearDiff = (b.year || 0) - (a.year || 0)
            if (yearDiff !== 0) return yearDiff
            return a.label.localeCompare(b.label, 'et')
        })

    const channelCategories = channelCategoriesData?.getChannelCategories.map(x => ({
        value: x.name,
        label: x.name,
    })) || []

    useEffect(() => {
        if (!show) {
            setErrors(InitialErrorsState)
            setSuccess(null)
            setCount('')
            setDistribution(null)
            setChannelCategory(null)
        }
    }, [show])

    const hasInputErrors = () => {
        let hasErrors = false
        const errorState = {
            ...InitialErrorsState,
        }

        if (!count) {
            hasErrors = true
            errorState.count = t('views.fill_field')
        }

        if (!distribution) {
            hasErrors = true
            errorState.distribution = t('views.fill_field')
        }

        if (distributions.find(x => x.value === distribution)?.type === 'TV' && !channelCategory) {
            hasErrors = true
            errorState.channelCategory = t('views.fill_field')
        }

        setErrors({
            ...errorState,
        })
        return hasErrors
    }

    const handleAdd = async () => {
        try {
            if (hasInputErrors()) return

            const dist = distributions.find(x => x.value === distribution)
            const channelCat = channelCategories.find(x => x.value === channelCategory)

            const res = await addCreationViews({
                variables: {
                    id: parseInt(creation.id, 10),
                    distribution: dist.value,
                    year: dist.year,
                    type: dist.type,
                    channelCategory: channelCat?.value,
                    count: parseInt(count, 10),
                    country: dist.country,
                },
            })

            if (!res?.data?.addCreationViews?.id) {
                setErrors({
                    ...errors,
                    generic: t('views.add_error'),
                })
                setSuccess(false)
                return
            }

            setSuccess(true)
            setCount('')
            setDistribution(null)
            setChannelCategory(null)
        } catch (err) {
            console.log(err)
            setSuccess(false)
            setErrors({
                ...errors,
                generic: t('views.add_error'),
            })
        }
    }

    return (
        <Modal
            className={'modal-add-creation-views'}
            show={show}
            close={close}
            title={`${t('views.add_views_to_creation')} "${creation.title}"`}
        >
            <div className='select-fields'>
                <SelectInput
                    options={distributions}
                    onChange={(option) => setDistribution(option.value)}
                    value={distributions.find(x => x.value === distribution) || null}
                    placeholder={t('filters.distribution')}
                    label={t('filters.distribution_label')}
                    error={errors.distribution}
                />
                {
                    distributions?.find(x => x.value === distribution)?.type === 'TV' ?
                        <SelectInput
                            options={channelCategories}
                            onChange={(option) => setChannelCategory(option.value)}
                            value={channelCategories.find(x => x.value === channelCategory)}
                            placeholder={t('views.channel_category')}
                            label={t('views.channel_category')}
                            error={errors.channelCategory}
                        />
                        : <></>
                }
                <Input
                    label={t('views.count')}
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                    type='number'
                    error={errors.count}
                />
            </div>
            <ErrorMessage message={errors?.generic} />
            <SuccessMessage message={success ? t('views.add_success') : null} />
            <div className='modal-actions'>
                <Button
                    label={t('views.close')}
                    className='btn-cancel'
                    onClick={close}
                />
                <Button
                    label={t('views.add_views')}
                    onClick={handleAdd}
                    loading={loading}
                    disabled={loading || !distribution || !count}
                />
            </div>
        </Modal>
    )
}

export default AddCreationViewsModal